import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import {
  FormGroup, ReactiveFormsModule, ValidationErrors, FormsModule, } from '@angular/forms';
import {
  NgbDatepickerModule, NgbDate, NgbDateParserFormatter, } from '@ng-bootstrap/ng-bootstrap';
import { GenericFormField } from '../model/generic-field';
import { LocalizationService } from '@abp/ng.core';
import { CommonModule } from '@angular/common';
import { ZendraLocalizationServices } from '../../enums/abpLocalizationServices';
import { DateMaskDirective } from '../../directives/date-mask.directive';
import { ZnSingleSelect } from '../../zn-select/zn-single-select';
import { localizeWithFallback } from '../../utils/localization-utils';
import { ZendraLocalizationPipe } from '../../pipes/zendra-localization.pipe';
import { DEFAULT_TIME_DATE } from '../../constants/numeric-constants';
import { CustomDateParserFormatter } from '../../utils/custom-date-parser-formatter';

@Component({
  selector: 'app-generic-field',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NgbDatepickerModule,
    ZendraLocalizationPipe,
    DateMaskDirective,
    ZnSingleSelect,
  ],
  providers: [{ provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }],
  templateUrl: './generic-field.html',
  styleUrl: './generic-field.scss',
})
export class GenericField implements OnInit {
  private static readonly TIME_NORMALIZE_REGEX = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;
  private static readonly TIME_FROM_ISO_REGEX = /T(\d{2}):(\d{2}):/;
  private static readonly DATE_ONLY_REGEX = /^(\d{4})[-/](\d{2})[-/](\d{2})(?:T.*)?$/;

  private readonly localizationService = inject(LocalizationService);
  field = input.required<GenericFormField>();
  form = input.required<FormGroup>();
  allFields = input<GenericFormField[]>([]);
  isEditMode = input(false);
  originalValues = input<Record<string, any>>({});
  localizationServices = ZendraLocalizationServices;
  readonly languageServiceResource = 'LanguageService';
  checked = signal(false);
  ngbDateValue = signal<NgbDate | null>(null);
  timeValue = signal<string>('');

  dropdownOptionsLoader = computed(() => {
    const loader = this.field().optionsLoader;
    if (loader) {
      return loader;
    }
    return undefined;
  });

  private readonly controlState = signal<{
    errors: ValidationErrors | null;
    touched: boolean;
    dirty: boolean;
  }>({
    errors: null,
    touched: false,
    dirty: false,
  });

  private readonly messageMap = computed(() => {
    const map = new Map<string, { message: string; value: any }>();

    for (const validator of this.field().validators ?? []) {
      map.set(validator.key, { message: validator.message, value: validator.value });
    }
    return map;
  });

  visibleErrors = computed(() => {
    this.controlState();
    const control = this.form().controls[this.field().key];
    const messageMap = this.messageMap();
    const controlErrors = control.errors as Record<string, any> | null;

    if (!control.invalid || !controlErrors) return [];

    return Object.keys(controlErrors)
      .map(errorKey => this.resolveError(errorKey, controlErrors[errorKey], messageMap))
      .filter((error): error is { message: string; value: any } => Boolean(error));
  });

  get showModified(): boolean {
    if (!this.isEditMode()) {
      return false;
    }

    const key = this.field().key;
    const control = this.form().controls[key];
    const originalValue = this.originalValues()[key];

    if (!control) {
      return false;
    }

    return !this.areValuesEquivalent(originalValue, control.value);
  }

  // Dropdowns in the last row open up instead of down
  openUp = computed(() => {
    const visibleFields = this.allFields().filter(f => !f.hidden);
    const total = visibleFields.length;

    if (total <= 4) {
      return false;
    }

    const index = visibleFields.findIndex(
      f => f.key === this.field().key
    );

    if (index === -1) {
      return false;
    }

    const openCount = total % 2 === 1 ? 3 : 4;
    const openFromIndex = Math.max(4, total - openCount);

    return index >= openFromIndex;
  });

  private normalizeTimeToFixedDateTime(value: any): any {
    if (typeof value !== 'string') return value;
    const t = value.trim();
    if (!t) return value;

    if (/^\d{4}-\d{2}-\d{2}T/.test(t)) return t;

    const m = GenericField.TIME_NORMALIZE_REGEX.exec(t);
    if (!m) return value;

    const ss = m[3] ?? '00';
    return `${DEFAULT_TIME_DATE}T${m[1]}:${m[2]}:${ss}`;
  }

  ngOnInit(): void {
    this.initializeReadOnly();
    this.initializeCheckbox();
    this.initializeDateControl();
    this.initializeTimeControl();
    this.form().controls[this.field().key].valueChanges.subscribe(() => {
      this.controlState.set({
        errors: this.errors,
        touched: this.isTouched,
        dirty: this.isDirty,
      });
    });
  }

  private initializeReadOnly(): void {
    if (!this.field().readOnly) {
      return;
    }

    const control = this.form().controls[this.field().key];
    if (control?.enabled) {
      control.disable({ emitEvent: false });
    }
  }

  private initializeCheckbox(): void {
    if (this.field().controlType !== 'checkbox') {
      return;
    }

    this.isChecked(this.field().value);
  }

  private initializeDateControl(): void {
    if (this.field().controlType !== 'datebox') {
      return;
    }

    const control = this.form().controls[this.field().key];
    const initialValue = this.field().value;

    if (!initialValue) {
      if (this.isDateTimeMode) {
        this.timeValue.set('00:00');
      }
      return;
    }

    if (this.isDateTimeMode) {
      this.setVisualDateAndTimeFromIso(initialValue);
      control.setValue(initialValue, { emitEvent: false });
      return;
    }

    const dateOnly = this.extractDateOnly(initialValue);
    if (!dateOnly) {
      this.ngbDateValue.set(null);
      control.setValue(initialValue, { emitEvent: false });
      return;
    }

    const [year, month, day] = dateOnly.split('-').map(x => Number.parseInt(x, 10));
    this.ngbDateValue.set(new NgbDate(year, month, day));
    control.setValue(dateOnly, { emitEvent: false });
  }

  private initializeTimeControl(): void {
    if (this.field().controlType !== 'timebox') {
      return;
    }

    const control = this.form().controls[this.field().key];
    const initialValue = this.field().value;
    if (!initialValue) {
      return;
    }

    const timeString = this.extractTimeFromValue(initialValue);
    this.timeValue.set(timeString);
    control.setValue(this.normalizeTimeToFixedDateTime(timeString), { emitEvent: false });
  }

  get isDirty() {
    return this.form().controls[this.field().key].dirty;
  }

  private extractTimeFromValue(value: any): string {
    if (!value) return '';

    if (typeof value === 'string' && value.includes('T')) {
      const timeMatch = GenericField.TIME_FROM_ISO_REGEX.exec(value);
      if (timeMatch) {
        const result = `${timeMatch[1]}:${timeMatch[2]}`;
        return result;
      }
    }

    if (typeof value === 'string' && /^\d{2}:\d{2}:\d{2}/.test(value)) {
      return value.substring(0, 5);
    }

    if (typeof value === 'string' && /^\d{2}:\d{2}$/.test(value)) {
      return value;
    }

    return '';
  }

  get isTouched() {
    return this.form().controls[this.field().key].touched;
  }

  get errors() {
    return this.form().controls[this.field().key].errors;
  }
  get valid() {
    return this.form().controls[this.field().key].valid;
  }
  get invalid() {
    return this.form().controls[this.field().key].invalid;
  }

  get isDateTimeMode(): boolean {
    return !!(this.field() as any).includeTime
      || (this.field() as any).type === 'datetime-local';
  }

  private toDateOnlyString(ngbDate: NgbDate): string {
    const yyyy = String(ngbDate.year);
    const mm = String(ngbDate.month).padStart(2, '0');
    const dd = String(ngbDate.day).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private extractDateOnly(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const match = GenericField.DATE_ONLY_REGEX.exec(value);
    if (!match) {
      return null;
    }

    const [, yyyy, mm, dd] = match;
    return `${yyyy}-${mm}-${dd}`;
  }

  private setControlDateValue(control: any, ngbDate: NgbDate): void {
    if (this.isDateTimeMode) {
      control.setValue(this.combineLocalDateAndTimeToUtcIso(ngbDate, this.timeValue()));
      return;
    }

    control.setValue(this.toDateOnlyString(ngbDate));
  }

  private combineLocalDateAndTimeToUtcIso(date: NgbDate, timeHHmm: string): string {
    const [hh, mm] = (timeHHmm || '00:00').split(':').map(x => Number.parseInt(x, 10));
    const local = new Date(date.year, date.month - 1, date.day, hh || 0, mm || 0, 0, 0);
    return local.toISOString();
  }

  private setVisualDateAndTimeFromIso(value: string) {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return;

    this.ngbDateValue.set(new NgbDate(d.getFullYear(), d.getMonth() + 1, d.getDate()));
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    this.timeValue.set(`${hh}:${mm}`);
  }

  // Determine if a checkbox field is checked by updating the checked property on the HTML
  isChecked(value: string | boolean): void {
    const boolValue =
      typeof value === 'string' ? value.toLowerCase() === 'true' : !!value;

    this.checked.set(boolValue);
    this.form().patchValue({ [this.field().key]: boolValue });
  }

  // Toggle a checkbox field and update the value
  toggle() {
    this.checked.set(!this.checked());
    this.form().patchValue({ [this.field().key]: this.checked() });
  }

  // Trim whitespace from text fields when they lose focus
  trimValue(): void {
    const control = this.form().controls[this.field().key];
    const value = control.value;

    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      if (value !== trimmedValue) {
        control.setValue(trimmedValue);
      }
    }
  }

  onDateChange(ngbDate: NgbDate | null): void {
    this.ngbDateValue.set(ngbDate);
    if (ngbDate === null) {
      this.form().controls[this.field().key].setValue(null);
      this.form().controls[this.field().key].markAsTouched();
    }
  }

  onDateSelect(ngbDate: NgbDate): void {
    if (ngbDate?.year && ngbDate.month && ngbDate.day) {
      this.ngbDateValue.set(ngbDate);
      const control = this.form().controls[this.field().key];

      this.setControlDateValue(control, ngbDate);

      if (control.hasError('date')) {
        control.setErrors(null);
      }
    }
  }

  onDateBlur(inputElement: HTMLInputElement): void {
    const rawValue = inputElement.value;
    const control = this.form().controls[this.field().key];

    if (rawValue?.length === 10) {
      const parts = rawValue.split('/');
      if (parts.length === 3) {
        const day = Number.parseInt(parts[0], 10);
        const month = Number.parseInt(parts[1], 10);
        const year = Number.parseInt(parts[2], 10);

        const testDate = new Date(year, month - 1, day);
        const isRealDate =
          testDate.getFullYear() === year &&
          testDate.getMonth() === month - 1 &&
          testDate.getDate() === day;

        if (isRealDate) {
          const ngbDate = new NgbDate(year, month, day);
          this.ngbDateValue.set(ngbDate);
          this.setControlDateValue(control, ngbDate);
        } else {
          // Invalid date (30/02/2025, 99/99/9999, etc) - set error and DON'T update value
          control.setErrors({ date: true });
        }
      }
    }

    this.markAsTouched();
  }

  private resolveError(
    errorKey: string,
    errorDetail: any,
    messageMap: Map<string, { message: string; value: any }>
  ): { message: string; value: any } | undefined {
    const errorInfo = messageMap.get(errorKey);
    if (!errorInfo) {
      return undefined;
    }

    if (
      errorKey === 'lessThanField' || errorKey === 'greaterThanField' ||
      errorKey === 'equalOrLessThanField' || errorKey === 'equalOrGreaterThanField'
    ) {
      return this.resolveCrossFieldError(errorInfo);
    }

    const detail = this.extractErrorDetail(errorDetail);
    if (detail.messageKey) {
      return { message: detail.messageKey, value: detail.value };
    }

    return errorInfo;
  }

  private resolveCrossFieldError(errorInfo: { message: string; value: any }): { message: string; value: any } {
    const relatedFieldKey = errorInfo.value;
    const relatedField = this.allFields().find(f => f.key === relatedFieldKey);
    if (!relatedField) {
      return errorInfo;
    }

    const localizedLabel = localizeWithFallback(this.localizationService, relatedField.label);
    return { message: errorInfo.message, value: localizedLabel };
  }

  private extractErrorDetail(errorDetail: any): { messageKey?: string; value?: any } {
    const detailIsObject = errorDetail && typeof errorDetail === 'object';
    if (!detailIsObject) {
      return {};
    }

    return {
      messageKey: errorDetail.messageKey,
      value: errorDetail.value,
    };
  }

  markAsTouched(): void {
    const control = this.form().controls[this.field().key];
    control.markAsTouched();
    this.controlState.set({
      errors: this.errors,
      touched: this.isTouched,
      dirty: this.isDirty,
    });
  }

  onTimeChange(event: any): void {
    this.timeValue.set(event || '');

    const control = this.form().controls[this.field().key];

    const date = this.ngbDateValue();
    if (date && event) {
      control.setValue(this.combineLocalDateAndTimeToUtcIso(date, event));
    } else if (date && !event) {
      control.setValue(this.combineLocalDateAndTimeToUtcIso(date, '00:00'));
    } else if (event) {
      control.setValue(this.normalizeTimeToFixedDateTime(event));
    } else {
      control.setValue(null);
    }

    this.markAsTouched();
  }

  getMaxLength(field: any): number | null {
    if (field.type === 'number' && field.decimalPrecision) {
      return field.decimalPrecision + 1;
    }

    if (field.type === 'text' || field.controlType === 'textarea') {
      return field.validators?.find(v => v.key === 'maxlength')?.value || null;
    }

    return null;
  }

  onKeyDown(event: KeyboardEvent): void {
    const inputElement = event.target as HTMLInputElement;
    const isNumberInput = inputElement.type === 'number';

    const hasNoStep =
      !inputElement.step || inputElement.step === '1' || inputElement.step === '';
    if ((isNumberInput && hasNoStep)) {
      if (event.key === '.' || event.key === ',' || event.key === 'e') {
        event.preventDefault();
      }
    }
    if (isNumberInput && (event.key === 'e' || event.key === 'E')) {
      event.preventDefault();
    }
  }

  private areEqual(a: any, b: any): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  private areValuesEquivalent(originalValue: any, currentValue: any): boolean {
    if (this.field().controlType === 'datebox') {
      return this.areDateValuesEquivalent(originalValue, currentValue);
    }

    if (this.field().controlType === 'timebox') {
      return this.areTimeValuesEquivalent(originalValue, currentValue);
    }

    return this.areEqual(originalValue, currentValue);
  }

  private areDateValuesEquivalent(originalValue: any, currentValue: any): boolean {
    if (this.isDateTimeMode) {
      return this.areEqual(this.normalizeIsoDateTime(originalValue), this.normalizeIsoDateTime(currentValue));
    }

    const normalizedOriginal = this.extractDateOnly(originalValue);
    const normalizedCurrent = this.extractDateOnly(currentValue);

    if (normalizedOriginal || normalizedCurrent) {
      return normalizedOriginal === normalizedCurrent;
    }

    return this.areEqual(originalValue, currentValue);
  }

  private areTimeValuesEquivalent(originalValue: any, currentValue: any): boolean {
    const normalizedOriginal = this.extractTimeFromValue(originalValue);
    const normalizedCurrent = this.extractTimeFromValue(currentValue);

    if (normalizedOriginal || normalizedCurrent) {
      return normalizedOriginal === normalizedCurrent;
    }

    return this.areEqual(originalValue, currentValue);
  }

  private normalizeIsoDateTime(value: any): any {
    if (typeof value !== 'string') {
      return value;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return parsed.toISOString();
  }
}
